import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthContextType, AuthUser, UserRole, UserRoleType, LoginRequest } from '../interfaces/Auth';
import { authService } from '../services/authService';
import { decodeJWT, isTokenExpired, JWTPayload } from '../utils/jwt';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const loadUserFromToken = () => {
    const token = localStorage.getItem('access_token');
    
    if (!token || isTokenExpired(token)) {
      logout();
      return;
    }

    const payload = decodeJWT(token);
    if (!payload) {
      logout();
      return;
    }

    const authUser: AuthUser = {
      id: payload.id,
      username: payload.username,
      lastname: payload.lastname,
      email: payload.email,
      role: payload.role as UserRoleType,
      is_active: true
    };

    const rolePermissions = createRoleFromType(payload.role as UserRoleType);
    
    setUser(authUser);
    setRole(rolePermissions);
    setPermissions(payload.permissions);
  };

  useEffect(() => {
    loadUserFromToken();
    setLoading(false);
  }, []);

  const generatePermissions = (userRole: UserRoleType): string[] => {
    return [];
  };

  const createRoleFromType = (roleType: UserRoleType): UserRole => {
    return {
      admin: roleType === 'admin',
      gestor: roleType === 'gestor' || roleType === 'admin',
      emissor: roleType === 'emissor' || roleType === 'gestor' || roleType === 'admin',
      reader: roleType === 'reader' || roleType === 'emissor' || roleType === 'gestor' || roleType === 'admin'
    };
  };

  const loginWithGoogle = async (googleToken: string) => {
    setLoading(true);
    try {
      const response = await authService.loginWithGoogle(googleToken);
      localStorage.setItem('access_token', response.access_token);
      loadUserFromToken();
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials: LoginRequest) => {
    setLoading(true);
    try {
      const response = await authService.login(credentials);
      localStorage.setItem('access_token', response.access_token);
      loadUserFromToken();
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    setUser(null);
    setRole(null);
    setPermissions([]);
  };

  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  const hasRole = (requiredRole: UserRoleType): boolean => {
    if (!user) return false;
    
    if (user.role === 'admin') return true;
    
    if (user.role === 'gestor' && (requiredRole === 'emissor' || requiredRole === 'gestor')) return true;
    
    return user.role === requiredRole;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        permissions,
        login,
        loginWithGoogle,
        logout,
        loading,
        hasPermission,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};